import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlayOpen } from '@/lib/overlay';
import { usePurchaseBarOpen } from '@/lib/purchaseBar';
import { isPrivatePath } from '@/lib/privateRoutes';

/**
 * GOHIGHLEVEL CHAT LAUNCHER GATE
 *
 * The LeadConnector chat widget is loaded site-wide from index.html and
 * renders its own launcher outside React. This component renders nothing; it
 * only toggles the `gg-chat-hidden` class on <html>, and src/index.css hides
 * the widget while that class is present.
 *
 * Hidden when:
 *  - on a private route (dashboard, admin, login, register, profile…)
 *    — same list as the floating stack, see src/lib/privateRoutes.ts
 *  - any registered overlay is open (modals, checkout, full-screen forms)
 *    — src/lib/overlay.ts
 *  - the sticky lead purchase bar is on screen — src/lib/purchaseBar.ts
 *
 * It never opens, closes or reconfigures the widget and touches no checkout,
 * payment or registration logic.
 */
const HIDDEN_CLASS = 'gg-chat-hidden';

const ChatWidgetGate: React.FC = () => {
  const { pathname } = useLocation();
  const overlayOpen = useOverlayOpen();
  const purchaseBar = usePurchaseBarOpen();

  const hidden = isPrivatePath(pathname) || overlayOpen || purchaseBar;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle(HIDDEN_CLASS, hidden);
  }, [hidden]);

  // Never leave the launcher hidden if the shell unmounts.
  useEffect(() => () => document.documentElement.classList.remove(HIDDEN_CLASS), []);

  return null;
};

export default ChatWidgetGate;
